/**
 * Configuration d'optimisation des images - Style "Application Professionnelle"
 * Inspiré des standards utilisés par Discord, Slack, Notion, etc.
 */

export interface ImageOptimizationConfig {
  // Dimensions maximales
  maxWidth: number;
  maxHeight: number;
  
  // Qualité des images (0.1 à 1.0)
  quality: number;
  
  // Taille maximale en KB
  maxSizeKB: number;
  
  // Format de compression
  compressionFormat: 'smart' | 'jpeg' | 'png' | 'webp';
  
  // Upload concurrent
  maxConcurrentUploads: number;
  
  // Seuils pour la compression adaptative
  highQualityThreshold: number; // Au-dessus de cette taille, compression agressive
  mediumQualityThreshold: number; // Taille moyenne
}

export const IMAGE_OPTIMIZATION_PROFILES = {
  // Profil pour screenshots et images techniques (qualité maximale)
  SCREENSHOT: {
    maxWidth: 1920,
    maxHeight: 1080,
    quality: 0.95, // 95% - qualité excellente
    maxSizeKB: 800,
    compressionFormat: 'smart' as const,
    maxConcurrentUploads: 3,
    highQualityThreshold: 1000, // 1MB
    mediumQualityThreshold: 500, // 500KB
  },
  
  // Profil pour photos et images générales (bon équilibre)
  PHOTO: {
    maxWidth: 1600,
    maxHeight: 1200,
    quality: 0.88,
    maxSizeKB: 600,
    compressionFormat: 'smart' as const,
    maxConcurrentUploads: 3,
    highQualityThreshold: 800,
    mediumQualityThreshold: 400,
  },
  
  // Profil pour miniatures et aperçus (optimisé taille)
  THUMBNAIL: {
    maxWidth: 400,
    maxHeight: 400,
    quality: 0.82,
    maxSizeKB: 100,
    compressionFormat: 'jpeg' as const,
    maxConcurrentUploads: 5,
    highQualityThreshold: 200,
    mediumQualityThreshold: 100,
  }
} as const;

// Configuration par défaut pour les trades (priorité à la qualité)
export const DEFAULT_TRADE_IMAGE_CONFIG = IMAGE_OPTIMIZATION_PROFILES.SCREENSHOT;

/**
 * Obtient la configuration optimale selon le contexte
 */
export function getOptimalImageConfig(context: 'trade' | 'profile' | 'thumbnail' = 'trade'): ImageOptimizationConfig {
  switch (context) {
    case 'trade':
      return IMAGE_OPTIMIZATION_PROFILES.SCREENSHOT;
    case 'profile':
      return IMAGE_OPTIMIZATION_PROFILES.PHOTO;
    case 'thumbnail':
      return IMAGE_OPTIMIZATION_PROFILES.THUMBNAIL;
    default:
      return IMAGE_OPTIMIZATION_PROFILES.SCREENSHOT;
  }
}

/**
 * Ajuste dynamiquement la qualité selon la taille du fichier original
 */
export function getAdaptiveQuality(originalSizeKB: number, config: ImageOptimizationConfig): number {
  if (originalSizeKB > config.highQualityThreshold) {
    // Fichier volumineux : réduction modérée de la qualité
    return Math.max(0.75, config.quality * 0.85);
  } else if (originalSizeKB > config.mediumQualityThreshold) {
    // Fichier moyen : légère réduction de la qualité
    return Math.max(0.85, config.quality * 0.95);
  } else {
    // Fichier petit : garder la qualité maximale
    return config.quality;
  }
} 